import pick from 'lodash/pick';
import StateController from 'panel/build/component-utils/state-controller';

import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';

export class DashboardModelController extends StateController {
  constructor({model, selectorController, dashboardsClient}) {
    super(...arguments);
    this.parentController = selectorController;
    this.client = dashboardsClient;
    this._update(model);
  }

  get defaultState() {
    return {
      id: null,
      title: null,
      description: null,
      creator_id: null,
      creator_name: null,
      creator_email: null,
      card_order: null,
      cards: null,
      time_filter: null,
      filters: null,
      loaded: false,
      error: null,

      // permissions
      can_view: false,
      can_update_basic: true,
      can_share: false,
      is_superadmin: false,
      allow_staff_override: false,
    };
  }

  _updateCachedModel(results) {
    this._update(results);
    this.parentController._updateCachedDashboard(this.state.id, results);
  }

  getModel() {
    const model = pick(this.state, [
      `id`,
      `title`,
      `description`,
      `creator_id`,
      `creator_name`,
      `creator_email`,
      `card_order`,
      `cards`,
      `filters`,
      `time_filter`,

      // permissions
      `can_view`,
      `can_update_basic`,
      `can_share`,
      `is_superadmin`,
      `allow_staff_override`,

      `is_favorited`,
    ]);
    if (model.time_filter && Object.keys(model.time_filter).length > 0) {
      const timeFilter = model.time_filter;
      timeFilter.dateRange = new DateRange(timeFilter.dateRange);
      model.time_filter = timeFilter;
    } else {
      model.time_filter = {};
    }

    if (model.filters) {
      model.filters = model.filters.map((f) => new PropertyFilter(f));
    } else {
      model.filters = [];
    }

    return {
      ...model,
    };
  }

  getLoaded() {
    return this.state.loaded;
  }

  // redundant method that is more consistent with out app verbiage
  isLoading() {
    return !this.state.loaded;
  }

  getError() {
    return this.state.error;
  }

  async loadModel() {
    this._update({loaded: false, error: null});
    // prevent redundant network requests
    if (!this._loadModelPromises) {
      this._loadModelPromises = this.client.querySingle(
        {
          projectId: this.parentController.projectId,
          dashboardID: this.state.id,
          workspaceId: this.parentController.workspaceId,
        },
        {clearCache: true},
      );
      Object.values(this._loadModelPromises).forEach((promise) =>
        promise
          .then((dashboard) => {
            dashboard && this._update({...dashboard, loaded: true, error: null});
          })
          .catch((error) => this._update({loaded: false, error})),
      );
    }

    // fallback to network if cached value doesn't exist
    (await this._loadModelPromises.cached) || (await this._loadModelPromises.network);

    Promise.all(Object.values(this._loadModelPromises))
      .catch((err) => {
        throw err;
      })
      .finally(() => (this._loadModelPromises = null));
  }

  async updateFields({fields = {}} = {}) {
    const originalModel = this.getModel();
    try {
      const newProposedModel = {
        ...originalModel,
        ...fields,
        cards: [...originalModel.cards, ...fields.cards],
      };
      await this._updateCachedModel(newProposedModel);

      const results = await this.client.patchUpdateDashboardFields({
        projectId: this.parentController.projectId,
        workspaceId: this.parentController.workspaceId,
        dashboardID: this.state.id,
        fields,
      });

      this._updateCachedModel(results);
    } catch (error) {
      this._updateCachedModel(originalModel);
      throw error;
    }
  }

  async favoriteDashboard(dashboardID, isFavorited) {
    await this.client.favoriteDashboard({
      projectId: this.parentController.projectId,
      workspaceId: this.parentController.workspaceId,
      dashboardID,
      isFavorited,
    });
    this._updateCachedModel({...this.getModel(), is_favorited: isFavorited});
  }
}
